<template>
    <div class="list-response">
        <template v-if="responseSet.type === 'dropdown'">
            <div
                class="w-full form-select mb-3"
                :class="{'empty': !selectedTitles}"
                @click="showDropdown">
                <template v-if="selectedTitles">
                    {{ selectedTitles.join(', ') }}
                </template>
                <template v-else>
                    {{ $t('Select from dropdown') }}
                </template>

            </div>

        </template>
        <template v-else>
            <template v-for="response in responseSet.responses">
                <ListResponse
                    :key="response.uuid"
                    :response="response"
                    :selected="
                        item.response
                            ? item.response.includes(response.uuid)
                            : false
                    "
                    @click="responseClicked"
                />
            </template>
        </template>

        <div
            v-if="item.notes_allowed !== false || item.photos_allowed !== false || item.issues_allowed !== false"
            style="height: 2rem; margin-top:0.4rem">
            <button
                v-if="item.notes_allowed !== false"
                class="w-16"
                style="height: 2rem;"
                type="button"
                @click="showNotes">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36">
                    <path :fill="notesIconFill"
                          d="M2 15V5c0-1.1.9-2 2-2h16a2 2 0 0 1 2 2v15a1 1 0 0 1-1.7.7L16.58 17H4a2 2 0 0 1-2-2zM20 5H4v10h13a1 1 0 0 1 .7.3l2.3 2.29V5z" />
                </svg>

            </button>

            <button
                v-if="item.photos_allowed !== false"
                type="button"
                class="w-16"
                style="height: 2rem;"
                @click="showPhotos">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36">
                    <path :fill="photosIconFill"
                          d="M20 7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9c0-1.1.9-2 2-2h2.38l1.73-3.45A1 1 0 0 1 9 3h6a1 1 0 0 1 .9.55L17.61 7H20zM9.62 5L7.89 8.45A1 1 0 0 1 7 9H4v10h16V9h-3a1 1 0 0 1-.9-.55L14.39 5H9.62zM12 17a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
                </svg>
            </button>

            <button
                v-if="issuesAllowed"
                type="button"
                class="w-16"
                style="height: 2rem;"
                @click="raiseIssueButtonClicked">
                <BellIcon
                    width="36"
                    height="36"
                    :class="raiseIssueButtonClasses"
                />
            </button>
        </div>

        <portal to="modals">
            <transition name="fade">
                <DropdownModal
                    v-if="dropdownVisible"
                    :responses="responseSet.responses"
                    :response="item.response"
                    :multiple="item.params.multiple_selection"
                    @save="saveDropdown"
                    @close="closeDropdown"
                />
            </transition>

            <transition name="fade">
                <RaiseIssueModal
                    v-if="isRaiseIssueModalVisible"
                    :questionId="item.uuid"
                    @close="raiseIssueModalClosed"
                />
            </transition>
        </portal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import ListResponse from '@/components/ConductInspection/ConductInspectionItem/ConductInspectionItemQuestion/ConductInspectionItemQuestionListResponse.vue';
    import DropdownModal from '@/views/ConductInspection/Modals/DropdownModal.vue';
    import RaiseIssueModal from '@/views/ConductInspection/Modals/RaiseIssueModal.vue';
    import BellIcon from '@/components/Icons/BellIcon.vue';
    import OnFreePlan from '@/mixins/OnFreePlan';
    import { ISSUE_PERMISSIONS } from '@/constants';

    export default {
        components: {
            ListResponse,
            DropdownModal,
            RaiseIssueModal,
            BellIcon,
        },

        mixins: [
            OnFreePlan,
        ],

        props: {
            item: {
                type: Object,
                required: true,
            },
            responseSets: {
                type: Array,
                required: true,
            },
        },

        data: () => ({
            dropdownVisible: false,
            isRaiseIssueModalVisible: false,
        }),
        computed: {
            ...mapGetters({
                inspection: 'conduct/inspection',
                hasPermission: 'authentication/hasPermission',
            }),

            issuesAllowed() {
                return this.item.issues_allowed !== false && this.hasPermission(
                    ISSUE_PERMISSIONS.CREATE_PERMISSION_NAME,
                );
            },

            responseSet() {
                for (const i in this.responseSets) {
                    if (this.responseSets[i].uuid === this.item.params.response_set) {
                        return this.responseSets[i];
                    }
                }
                return [];
            },

            notesIconFill() {
                if (this.item.notes) {
                    return 'var(--primary)';
                }
                return 'var(--60)';
            },

            photosIconFill() {
                if (this.item.photos && this.item.photos.length > 0) {
                    return 'var(--primary)';
                }
                return 'var(--60)';
            },

            raiseIssueButtonClasses() {
                const isIssueExistsForInspection = this.inspection.issues
                    && Object.keys(this.inspection.issues).find((uuid) => uuid === this.item.uuid);
                return {
                    'text-60': !isIssueExistsForInspection,
                    'text-primary': isIssueExistsForInspection,
                };
            },

            selectedTitles() {
                if (this.item.responded && this.item.response && this.item.response.length > 0) {
                    const titles = [];
                    for (const i in this.responseSet.responses) {
                        if (this.item.response.includes(this.responseSet.responses[i].uuid)) {
                            titles.push(this.responseSet.responses[i].title);
                        }
                    }
                    return titles;
                }
            },
        },

        methods: {
            raiseIssueButtonClicked() {
                this.isRaiseIssueModalVisible = true;
            },

            raiseIssueModalClosed() {
                this.isRaiseIssueModalVisible = false;
            },

            responseClicked(value) {
                let { response } = this.item;

                if (typeof (response) === 'undefined') {
                    response = [];
                }

                const index = response.indexOf(value);
                if (index > -1) {
                    response.splice(index, 1);
                } else {
                    if (!this.item.params.multiple_selection) {
                        response = [];
                    }
                    response.push(value);
                }

                this.$store.commit('conduct/changeResponse', {
                    item: this.item.uuid,
                    response,
                    responded: (response.length > 0),
                });
            },

            showNotes() {
                this.$store.commit('conduct/showNotesModal', this.item);
            },

            showPhotos() {
                this.$store.commit('conduct/showPhotosModal', this.item);
            },

            showDropdown() {
                this.dropdownVisible = true;
            },

            closeDropdown() {
                this.dropdownVisible = false;
            },

            saveDropdown(response) {
                this.$store.commit('conduct/changeResponse', {
                    item: this.item.uuid,
                    response,
                    responded: (response.length > 0),
                });
                this.dropdownVisible = false;
            },

        },
    };
</script>
